import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Briefcase, Plus, Loader2, Settings2, Trash2, ArrowLeft, Plug, Edit2, Sparkles, FileSpreadsheet, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { ImportClientsDialog } from '../components/ImportClientsDialog';
import { ClientReportDialog } from '../components/ClientReportDialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const PRESET_COLORS = [
  '#6366f1', '#dc2626', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#64748b',
];

const EMPTY_FORM = {
  id: null,
  name: '',
  brand_color: '#6366f1',
  notes: '',
  default_style_profile_id: '',
  review_email: '',
  review_contact_name: '',
};

export const ClientsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Unified create + edit dialog state
  const [editing, setEditing] = useState(null); // null = closed; {...} = open
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [styleProfiles, setStyleProfiles] = useState([]);

  // Manage destinations state
  const [manageClient, setManageClient] = useState(null);
  const [reportClient, setReportClient] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const [c, sp] = await Promise.all([
        api.get('/clients'),
        api.get('/style-profiles'),
      ]);
      setClients(c.data?.clients || []);
      setStyleProfiles(sp.data?.profiles || []);
    } catch (_) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing({});
  };

  const openEdit = (c) => {
    setForm({
      id: c.id,
      name: c.name || '',
      brand_color: c.brand_color || '#6366f1',
      notes: c.notes || '',
      default_style_profile_id: c.default_style_profile_id || '',
      review_email: c.review_email || '',
      review_contact_name: c.review_contact_name || '',
    });
    setEditing(c);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      brand_color: form.brand_color,
      notes: form.notes.trim() || null,
      default_style_profile_id: form.default_style_profile_id || null,
      review_email: form.review_email.trim() || '',
      review_contact_name: form.review_contact_name.trim() || '',
    };
    try {
      if (editing?.id) {
        // Edit — the backend treats `""` as a "clear" sentinel for the profile id.
        await api.put(`/clients/${editing.id}`, {
          ...payload,
          default_style_profile_id: form.default_style_profile_id || '',
        });
        toast.success('Client updated');
      } else {
        await api.post('/clients', payload);
        toast.success('Client created');
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      await fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (client) => {
    if (!window.confirm(`Archive "${client.name}"? Existing posts keep their tag for reporting.`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success('Client archived');
      await fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-indigo-600" />
            Clients
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Group your connected destinations by client. Filter the dashboard, calendar, and AI campaigns by client.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={() => setShowImport(true)}
            data-testid="import-clients-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button
            onClick={openCreate}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New client
          </Button>
        </div>
      </div>

      {/* Quick action: connect a new FB/IG account without leaving the
          mental model of the Client Manager. Lands on /connections where the
          user can sign into a different account. */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-4 py-3"
        data-testid="connect-fbig-cta"
      >
        <div className="flex items-start sm:items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md bg-white border border-zinc-200 flex items-center justify-center shrink-0">
            <Plug className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-zinc-800">
              Manage another Facebook or Instagram account
            </p>
            <p className="text-xs text-zinc-500">
              Connect more accounts and assign their pages to clients without disconnecting existing ones.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/connections')}
          data-testid="connect-fbig-btn"
          className="shrink-0 self-start sm:self-auto"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Connect FB / IG account
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : clients.length === 0 ? (
        <Card className="border-dashed border-zinc-200">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-700">No clients yet</p>
            <p className="text-xs text-zinc-500 mt-1 max-w-md mx-auto">
              Create a client (e.g. "Acme Corp") then assign their Facebook Pages, Instagram accounts, or LinkedIn pages to keep their content organized.
            </p>
            <Button onClick={openCreate} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add your first client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <Card
              key={c.id}
              className="border-zinc-200 hover:shadow-md transition-shadow overflow-hidden"
              data-testid={`client-card-${c.id}`}
            >
              <div className="h-1.5" style={{ background: c.brand_color || '#6366f1' }} />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-zinc-900 truncate">{c.name}</h3>
                  <Badge variant="outline" className="text-[10px]">
                    {c.destination_count} dest · {c.post_count} posts
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {c.notes && <p className="text-xs text-zinc-600 line-clamp-2">{c.notes}</p>}
                {c.default_style_profile_id && (
                  <div className="flex items-center gap-1 text-[11px] text-indigo-700" data-testid={`client-default-profile-${c.id}`}>
                    <Sparkles className="w-3 h-3" />
                    <span className="truncate">
                      Auto-style: {styleProfiles.find((p) => p.id === c.default_style_profile_id)?.name || 'Style profile'}
                    </span>
                  </div>
                )}
                <div className="flex gap-2 pt-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(c)}
                    data-testid={`edit-client-${c.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setManageClient(c)}
                    data-testid={`manage-client-${c.id}`}
                  >
                    <Settings2 className="w-3.5 h-3.5 mr-1" />
                    Manage destinations
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReportClient(c)}
                    data-testid={`report-client-${c.id}`}
                  >
                    <BarChart3 className="w-3.5 h-3.5 mr-1" />
                    Report
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleArchive(c)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit client' : 'New client'}</DialogTitle>
            <DialogDescription>Pick a name, brand color, and optionally a default style profile so AI forms auto-apply the right voice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="client-name">Name</Label>
              <Input
                id="client-name"
                placeholder="e.g. Acme Corp"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-client-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Brand color</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, brand_color: c })}
                    aria-label={`Pick color ${c}`}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      form.brand_color === c ? 'border-zinc-900 scale-110' : 'border-zinc-200'
                    }`}
                    style={{ background: c }}
                  />
                ))}
                <Input
                  type="color"
                  value={form.brand_color}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  className="w-12 h-8 p-1"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-default-profile" className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                Default style profile (auto-apply)
              </Label>
              <Select
                value={form.default_style_profile_id || '__none__'}
                onValueChange={(v) => setForm({ ...form, default_style_profile_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger id="client-default-profile" data-testid="client-default-profile-select">
                  <SelectValue placeholder="— No auto-apply —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No auto-apply —</SelectItem>
                  {styleProfiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500">
                When set, this style profile is auto-loaded on AI Post Manager whenever this client is selected.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-notes">Notes (optional)</Label>
              <Textarea
                id="client-notes"
                rows={2}
                placeholder="Internal notes — only you see these"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            {/* Magic-link client review (Agency Phase C) */}
            <div className="space-y-2 border-t border-zinc-200 pt-3">
              <Label className="text-sm font-semibold">Magic-link review</Label>
              <p className="text-[11px] text-zinc-500 -mt-1">
                When set, you can send drafts to this email for client approval — no account required.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="client-review-email" className="text-xs">Review email</Label>
                  <Input
                    id="client-review-email"
                    type="email"
                    placeholder="approver@acme.com"
                    value={form.review_email}
                    onChange={(e) => setForm({ ...form, review_email: e.target.value })}
                    data-testid="client-review-email-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="client-review-name" className="text-xs">Contact name (optional)</Label>
                  <Input
                    id="client-review-name"
                    placeholder="Jane Doe"
                    value={form.review_contact_name}
                    onChange={(e) => setForm({ ...form, review_contact_name: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setForm(EMPTY_FORM); }} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="create-client-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editing?.id ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageDestinationsDialog
        client={manageClient}
        onClose={() => { setManageClient(null); fetchClients(); }}
      />
      <ImportClientsDialog
        open={showImport}
        onOpenChange={setShowImport}
        onImported={fetchClients}
      />
      <ClientReportDialog
        client={reportClient}
        onClose={() => setReportClient(null)}
      />
    </div>
  );
};


// ============================================================
// Manage Destinations Dialog
// ============================================================
const ManageDestinationsDialog = ({ client, onClose }) => {
  const { api } = useAuth();
  const [allTargets, setAllTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Local set of "platform::account_id" currently assigned to THIS client
  const [assignedKeys, setAssignedKeys] = useState(new Set());

  const keyOf = (t) => `${t.platform}::${t.account_id}`;

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    api.get('/connections/targets').then((r) => {
      const targets = r.data?.targets || [];
      setAllTargets(targets);
      const initial = new Set(
        targets.filter((t) => t.client_id === client.id).map(keyOf)
      );
      setAssignedKeys(initial);
    }).finally(() => setLoading(false));
  }, [client, api]);

  const grouped = useMemo(() => {
    const map = {};
    for (const t of allTargets) (map[t.platform] = map[t.platform] || []).push(t);
    return map;
  }, [allTargets]);

  const toggle = (t) => {
    const k = keyOf(t);
    setAssignedKeys((cur) => {
      const next = new Set(cur);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const handleSave = async () => {
    if (!client) return;
    setBusy(true);
    try {
      // Diff: which destinations changed?
      const initial = new Set(
        allTargets.filter((t) => t.client_id === client.id).map(keyOf)
      );
      const toAssign = [];
      const toUnassign = [];
      for (const t of allTargets) {
        const k = keyOf(t);
        const wasAssigned = initial.has(k);
        const isAssigned = assignedKeys.has(k);
        if (!wasAssigned && isAssigned) toAssign.push({ platform: t.platform, account_id: t.account_id });
        if (wasAssigned && !isAssigned) toUnassign.push({ platform: t.platform, account_id: t.account_id });
      }
      if (toAssign.length > 0) {
        await api.post('/clients/assign', { client_id: client.id, destinations: toAssign });
      }
      if (toUnassign.length > 0) {
        await api.post('/clients/unassign', { destinations: toUnassign });
      }
      const total = toAssign.length + toUnassign.length;
      toast.success(total > 0 ? `Saved (${total} change${total === 1 ? '' : 's'})` : 'No changes');
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {client && (
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ background: client.brand_color || '#6366f1' }}
              />
            )}
            {client?.name} — Destinations
          </DialogTitle>
          <DialogDescription>
            Tick every destination this client owns. Tagged destinations stamp their client_id on every future scheduled post.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.keys(grouped).length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">
                No connected destinations yet. Connect platforms on /connections first.
              </p>
            ) : (
              Object.entries(grouped).map(([platform, items]) => (
                <div key={platform} className="space-y-1.5">
                  <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide capitalize">
                    {platform.replace('_', ' ')} · {items.length}
                  </p>
                  <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100">
                    {items.map((t) => {
                      const k = keyOf(t);
                      const checked = assignedKeys.has(k);
                      const otherClient = t.client_id && t.client_id !== client?.id;
                      return (
                        <label
                          key={k}
                          className={`flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-50 ${otherClient ? 'opacity-60' : ''}`}
                          data-testid={`manage-target-${platform}-${t.account_id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggle(t)}
                            disabled={otherClient && !checked}
                            className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                          />
                          <span className="flex-1 truncate text-zinc-700">{t.account_name}</span>
                          {otherClient && (
                            <span className="text-[10px] text-zinc-500" style={{ color: t.client_color }}>
                              {t.client_name}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Close
          </Button>
          <Button onClick={handleSave} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-destinations-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientsPage;
